import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Button, IconButton } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';

import Iconify from 'src/components/iconify';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import {
  IAdvancedPaymentTableFilters,
  IAdvancedPaymentTableFilterValue,
} from 'src/types/advanced-payment';

type Props = {
  filters: IAdvancedPaymentTableFilters;
  onFilters: (name: string, value: IAdvancedPaymentTableFilterValue) => void;
  handleResetFilters: VoidFunction;
  canReset: boolean;
};

export default function AdvancedPaymentsTableToolBar({
  filters,
  onFilters,
  handleResetFilters,
  canReset,
}: Props) {
  const [input, setInput] = useState<string>('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilters('generalSearch', value);
    }, 500),
    []
  );

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value);
  };

  const handleFilterStartDate = useCallback(
    (date: any) => {
      onFilters('startDate', date);
    },
    [onFilters]
  );

  const handleFilterEndDate = useCallback(
    (date: any) => {
      onFilters('endDate', date);
    },
    [onFilters]
  );

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    handleResetFilters();
  }, [handleResetFilters]);

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{
        xs: 'column',
        md: 'row',
      }}
      sx={{
        p: 2.5,
        pr: { xs: 2.5, md: 1 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} sx={{ width: 1 }}>
        <TextField
          fullWidth
          value={input}
          onChange={handleSearchChange}
          placeholder="Search for employee name, employee number..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment:
              input.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearchText}>
                    <Iconify icon="eva:close-fill" color="text.disabled" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          inputProps={{
            autoComplete: 'off',
          }}
        />
      </Stack>

      <CustomDatePicker
        disableFuture
        label="Start Date"
        value={filters.startDate}
        onChange={handleFilterStartDate}
      />
      <CustomDatePicker
        disableFuture
        label="End Date"
        value={filters.endDate}
        onChange={handleFilterEndDate}
      />

      {canReset && (
        <Button
          color="error"
          onClick={clearAll}
          variant="text"
          sx={{ textDecoration: 'underline' }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
